import {
  LoanActionResult,
  LoanEditorActionsEnum,
  loanActionTypesNeverPromptingAtExecution,
  MersDeactivationKind,
  mersDeactivationKindsNeedingLiquidationDate,
  mersPreviewTargetByOperation,
} from 'constants/loanActionEditor';

export const getLoanActionVariantOptionLabel = option => option?.name || '';

export const isSnapdocsCommentPromptAtExecution = ({ type, promptAtExecution }) =>
  type === LoanEditorActionsEnum.POST_SNAPDOCS_COMMENT && !!promptAtExecution;

export const isSnapdocsOptOutReasonPromptAtExecution = ({ type, promptAtExecution }) =>
  type === LoanEditorActionsEnum.OPT_OUT_SNAPDOCS_ESIGNING && !!promptAtExecution;

// Paid in Full and Foreclosure Complete ask for the liquidation date; Transfer to Non-MERS needs nothing.
export const isMersDeactivationPromptAtExecution = ({ type, kind }) =>
  type === LoanEditorActionsEnum.DEACTIVATE_MERS_MIN &&
  mersDeactivationKindsNeedingLiquidationDate.includes(kind);

// Every transfer asks for its effective date, which the loan does not store.
export const isMersTransferPromptAtExecution = ({ type }) =>
  type === LoanEditorActionsEnum.TRANSFER_WITH_MERS;

const isMersPromptAtExecution = loanActionType =>
  isMersDeactivationPromptAtExecution(loanActionType) ||
  isMersTransferPromptAtExecution(loanActionType);

const NEVER_PROMPTING_CONFIGURATION_BY_TYPE = {
  [LoanEditorActionsEnum.ASSIGN_LOAN_ROLE]: ({ allowUserSelection }) => !allowUserSelection,
  [LoanEditorActionsEnum.DEACTIVATE_MERS_MIN]: ({ kind }) =>
    kind === MersDeactivationKind.TRANSFER_TO_NON_MERS,
  [LoanEditorActionsEnum.POST_SNAPDOCS_COMMENT]: ({ promptAtExecution }) => !promptAtExecution,
  [LoanEditorActionsEnum.OPT_OUT_SNAPDOCS_ESIGNING]: ({ promptAtExecution }) => !promptAtExecution,
};

const neverPromptsAtExecution = loanActionType =>
  loanActionTypesNeverPromptingAtExecution.includes(loanActionType.type) ||
  !!NEVER_PROMPTING_CONFIGURATION_BY_TYPE[loanActionType.type]?.(loanActionType);

/**
 * A manual run carries one set of execution details and the runner opens one prompt, so a MERS item that prompts can only
 * share its action with items that never prompt (loanActionTypesNeverPromptingAtExecution plus the configurations above).
 * Mirrors LoanActionItemsValidator in Skynet.LoanProcessing, which refuses to save the same combinations.
 * @param {Array<{ type: string }>|null} loanActionTypes items in the API shape (type, kind, promptAtExecution, ...)
 * @returns {number} the index of the prompting MERS item that can't share its run, or -1 when the action can run
 */
export const findMersPromptConflictIndex = loanActionTypes => {
  const items = loanActionTypes ?? [];
  const mersPromptIndex = items.findIndex(isMersPromptAtExecution);

  if (mersPromptIndex === -1) {
    return -1;
  }

  const hasConflict = items.some(
    (loanActionType, index) =>
      index !== mersPromptIndex &&
      (isMersPromptAtExecution(loanActionType) || !neverPromptsAtExecution(loanActionType)),
  );

  return hasConflict ? mersPromptIndex : -1;
};

/**
 * The item the runner prompts for: a Preview MERS item stands in for the MERS action it previews, in that action's own
 * shape (type, kind, preClosing, flow), so a preview asks for and sends exactly the details the real action would.
 * Every other item is itself.
 * @param {{ type: string }} loanActionType an item in the API shape
 * @returns {{ type: string }} the item, or the previewed MERS action's item for a Preview MERS item
 */
export const getMersPromptItem = loanActionType => {
  if (loanActionType.type !== LoanEditorActionsEnum.PREVIEW_MERS) {
    return loanActionType;
  }

  const target = mersPreviewTargetByOperation[loanActionType.operation];

  return {
    type: target?.actionType ?? null,
    kind: target ? (loanActionType[target.kindField] ?? null) : null,
    preClosing: !!loanActionType.preClosing,
    flow: !!loanActionType.flow,
  };
};

/**
 * Preview MERS shows what one MERS action would send, so it must be the only item in its action. Mirrors the
 * LoanActionItemsValidator rule in Skynet.LoanProcessing.
 * @param {Array<{ type: string }>|null} loanActionTypes items in the API shape
 * @returns {number} the index of the Preview MERS item that shares its action, or -1 when the action can run
 */
export const findMersPreviewConflictIndex = loanActionTypes => {
  const items = loanActionTypes ?? [];

  return items.length > 1
    ? items.findIndex(({ type }) => type === LoanEditorActionsEnum.PREVIEW_MERS)
    : -1;
};

/**
 * The MERS preview a run returned. A preview sends and changes nothing, so its run shows the preview instead of completing,
 * refreshing or reloading anything.
 * @param {Array<{ type: string }>|null} results LoanActionResult[]
 * @returns {object|null} the MersPreview result, or null when the run was not a preview
 */
export const findMersPreviewResult = results =>
  (results ?? []).find(({ type }) => type === LoanActionResult.MERS_PREVIEW) ?? null;

// Whether an action's items hold a Preview MERS, which sends nothing, so its run needs no "Do you want to proceed?"
// confirmation and never completes a task.
export const hasMersPreviewItem = loanActionTypes =>
  (loanActionTypes ?? []).some(({ type }) => type === LoanEditorActionsEnum.PREVIEW_MERS);
